/**
 * @fileoverview AipAssistHeader — top row of the AIP Assist sidebar panel.
 * Layout: AIP logo avatar + "AIP Assist" title + an optional `pills` slot
 * (e.g. a minimal "7 days" tag + success auto-updates pill) on the left;
 * on the right, a Blueprint ButtonGroup with Plus (new chat), Chat (all
 * chats), a divider, and a small minimal Close button.
 *
 * Foundry mapping: `aip-assist-impl__header` →
 * `workspace-aip-assist-sidebar__sidebar-header` with `__left` / `__right`
 * children. The AIP logo uses Blueprint's `aip-logo` icon.
 */

import type { ReactElement, ReactNode } from "react";
import { Button, ButtonGroup, Divider, Icon } from "@blueprintjs/core";

interface Props {
  title?: string;
  pills?: ReactNode;
  onNewChat?: () => void;
  onShowAllChats?: () => void;
  onClose: () => void;
  className?: string;
}

export function AipAssistHeader({
  title = "IRIS Assistant",
  pills,
  onNewChat,
  onShowAllChats,
  onClose,
  className,
}: Props): ReactElement {
  return (
    <div data-testid="aip-assist-header" className={["bp-aip-assist-header", className].filter(Boolean).join(" ")}>
      <div className="bp-aip-assist-header-left">
        <span className="bp-aip-assist-avatar">
          <Icon icon="predictive-analysis" size={16} />
        </span>
        <div className="bp6-text-overflow-ellipsis bp-aip-assist-header-title">{title}</div>
        {pills && <span className="bp-aip-assist-header-pills">{pills}</span>}
      </div>
      <div className="bp-aip-assist-header-right">
        <ButtonGroup>
          {onNewChat && (
            <Button
              data-testid="aip-assist-header-new-chat"
              minimal
              icon="plus"
              aria-label="Create new chat"
              onClick={onNewChat}
            />
          )}
          {onShowAllChats && (
            <Button
              data-testid="aip-assist-header-all-chats"
              minimal
              icon="chat"
              aria-label="All chats"
              onClick={onShowAllChats}
            />
          )}
          <Divider />
          <Button
            data-testid="aip-assist-header-close"
            small
            minimal
            icon="cross"
            aria-label="Close IRIS Assistant"
            onClick={onClose}
          />
        </ButtonGroup>
      </div>
    </div>
  );
}
